/**
 * ============================================================================
 * Iron Meridian - Full Game Replay Component
 * ============================================================================
 * 
 * Comprehensive replay system that reuses existing game UI.
 * Loads turn snapshots and provides deterministic turn-by-turn playback.
 */

import { useMemo, useEffect } from 'react';
import { useReplay } from '../hooks/useReplay';
import GameLayout from './GameLayout';
import ReplayControls from './ReplayControls';

export default function FullGameReplay({ 
  gameId, 
  onExit,
  initialTurn = 0,
  turnHighlights = []
}) {
  const replay = useReplay(gameId);

  // Jump to initial turn if provided (from analytics)
  useEffect(() => {
    if (replay.replayData && initialTurn > 0) {
      replay.jumpToTurn(initialTurn);
    }
  }, [replay.replayData, initialTurn]);

  // Transform snapshot into game state format
  const gameState = useMemo(() => {
    if (!replay.currentTurn) return null;

    const snapshot = replay.currentTurn.snapshot;
    const humanPlayer = snapshot.players.find(p => p.type === 'human');
    
    return {
      gameId: gameId,
      currentTurn: replay.currentTurn.turn,
      phase: snapshot.game.phase,
      status: snapshot.game.status,
      playerCountry: humanPlayer ? 
        snapshot.countries.find(c => c.owner_player_id === humanPlayer.id)?.country_id :
        snapshot.countries[0]?.country_id,
      isReplayMode: true // Flag for UI components to disable interactions
    };
  }, [replay.currentTurn, gameId]);

  // Transform countries from snapshot
  const countries = useMemo(() => {
    if (!replay.currentTurn) return {};

    const snapshot = replay.currentTurn.snapshot;
    const countriesMap = {};

    for (const country of snapshot.countries) {
      countriesMap[country.country_id] = {
        id: country.country_id,
        name: country.name,
        owner_player_id: country.owner_player_id,
        military: country.military,
        economy: country.economy,
        technology: country.technology,
        stability: country.stability,
        terrain: country.terrain,
        region: country.region,
        controller: country.owner_player_id ? 
          (snapshot.players.find(p => p.id === country.owner_player_id)?.type || 'ai') : 
          'neutral',
        // For map display
        color: getPlayerColor(country.owner_player_id, snapshot.players)
      };
    }

    return countriesMap;
  }, [replay.currentTurn]);

  // Transform players from snapshot
  const players = useMemo(() => {
    if (!replay.currentTurn) return [];

    return replay.currentTurn.snapshot.players;
  }, [replay.currentTurn]);

  // Transform diplomacy from snapshot
  const diplomacyState = useMemo(() => {
    if (!replay.currentTurn) return { relationships: {} };

    const snapshot = replay.currentTurn.snapshot;
    const relationships = {};

    if (snapshot.diplomacy) {
      for (const relation of snapshot.diplomacy) {
        const key = `${relation.player_a_id}-${relation.player_b_id}`;
        relationships[key] = relation;
      }
    }

    return { relationships };
  }, [replay.currentTurn]);

  // Combat log for current turn
  const combatLog = useMemo(() => {
    return replay.currentCombat.map(combat => ({
      turn: combat.turn,
      attacker: combat.attackerCountry,
      defender: combat.defenderCountry,
      outcome: combat.outcome,
      attackerLosses: combat.attackerLosses,
      defenderLosses: combat.defenderLosses,
      territoryChanged: combat.territoryChanged
    }));
  }, [replay.currentCombat]);

  // Loading state
  if (replay.loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-war-room-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading replay data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (replay.error) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-war-room-900">
        <div className="max-w-md p-8 bg-war-room-800 rounded-lg border border-war-room-600/30">
          <div className="text-red-400 text-xl font-bold mb-4">Error Loading Replay</div>
          <p className="text-gray-400 mb-4">{replay.error}</p>
          <div className="flex gap-3">
            <button
              onClick={replay.reload}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Retry
            </button>
            <button
              onClick={onExit}
              className="px-4 py-2 bg-war-room-700 hover:bg-war-room-600 rounded-lg transition-colors"
            >
              Exit
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No data
  if (!replay.replayData || !gameState) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-war-room-900">
        <p className="text-gray-400">No replay data available</p>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen flex flex-col bg-war-room-900">
      {/* Main game view (readonly) */}
      <div className="flex-1 overflow-hidden">
        <GameLayout
          gameState={gameState}
          countries={countries}
          selectedCountry={null}
          actionQueue={[]} // Empty in replay mode
          combatLog={combatLog}
          turnProcessing={false}
          onCountrySelect={() => {}} // Disabled in replay
          onAddAction={() => {}} // Disabled in replay
          onRemoveAction={() => {}} // Disabled in replay
          onClearActions={() => {}} // Disabled in replay
          onProcessTurn={() => {}} // Disabled in replay
          onNewGame={onExit}
          onReturnToMenu={onExit}
          players={players}
          diplomacyState={diplomacyState}
          pendingProposals={[]}
          onProposeTreaty={() => {}} // Disabled in replay
          onAcceptProposal={() => {}} // Disabled in replay
          onRejectProposal={() => {}} // Disabled in replay
          onCancelTreaty={() => {}} // Disabled in replay
        />
      </div>

      {/* Replay controls overlay */}
      <ReplayControls
        currentTurnIndex={replay.currentTurnIndex}
        totalTurns={replay.totalTurns}
        isPlaying={replay.isPlaying}
        playbackSpeed={replay.playbackSpeed}
        isAtStart={replay.isAtStart}
        isAtEnd={replay.isAtEnd}
        onPlay={replay.play}
        onPause={replay.pause}
        onStepForward={replay.stepForward}
        onStepBackward={replay.stepBackward}
        onJumpToStart={replay.jumpToStart}
        onJumpToEnd={replay.jumpToEnd}
        onJumpToTurn={replay.jumpToTurn}
        onSpeedChange={replay.setSpeed}
        turnHighlights={turnHighlights}
      />
    </div>
  );
}

/**
 * Helper: Get player color for map display
 */
function getPlayerColor(playerId, players) {
  if (!playerId) return '#888888'; // Neutral

  const player = players.find(p => p.id === playerId);
  
  if (!player) return '#888888';

  // Assign consistent colors per player
  const colors = {
    human: '#3b82f6',    // Blue
    ai_1: '#ef4444',     // Red
    ai_2: '#10b981',     // Green
    ai_3: '#f59e0b',     // Orange
    ai_4: '#8b5cf6',     // Purple
    ai_5: '#ec4899',     // Pink
    ai_6: '#06b6d4',     // Cyan
    ai_7: '#84cc16'      // Lime
  };

  if (player.type === 'human') return colors.human;

  // Use player ID hash for consistent AI colors
  const index = parseInt(player.id.replace(/\D/g, '')) || 1;
  return colors[`ai_${(index % 7) + 1}`] || '#888888';
}
